import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { theme } from "uikit";

import Call from "../../services/Call";
import useModelSubscribe from "../../hooks/useModelSubscribe2";
import { SuspenseLoader } from "../common";

import Item from "./components/Item";
import useNow from "./hooks/useNow";
import { getStartTime, isLive } from "./utils";

/** Show the empty state even if the server never answers the subscription. */
const LOADING_TIMEOUT_MS = 10_000;

const Root = styled.div`
	position: absolute;
	inset: 0;
	overflow: hidden auto;
	background: #ffffff;
`;

const Empty = styled.div`
	padding: 24px 16px;
	text-align: center;
	font-size: 13px;
	color: ${theme.colors.secondary};
`;

const List: React.FC<{ orderId: number }> = ({ orderId }) => {
	const { t } = useTranslation();
	const [loaded, setLoaded] = useState(false);

	const options = useMemo<Call.SubscribeOptions>(
		() => ({ orderIds: [orderId], subscribeType: "front_archive" }),
		[orderId],
	);

	const onUpdate = useCallback(() => setLoaded(true), []);
	const { models } = useModelSubscribe(options, Call, {
		debounce: 0,
		onUpdate,
	});

	useEffect(() => {
		const timer = setTimeout(() => setLoaded(true), LOADING_TIMEOUT_MS);
		return () => clearTimeout(timer);
	}, []);

	const hasLive = useMemo(
		() => (models || []).some((call) => isLive(call, Date.now())),
		[models],
	);
	const now = useNow(hasLive);

	const calls = useMemo(
		() =>
			(models || [])
				.filter(Boolean)
				.sort(
					(a, b) =>
						(getStartTime(b) || 0) - (getStartTime(a) || 0) ||
						b.id - a.id,
				),
		[models],
	);

	if (!calls.length) {
		if (!loaded) return <SuspenseLoader />;
		return <Empty>{t("change_history.calls.empty")}</Empty>;
	}

	return (
		<>
			{calls.map((call) => (
				<Item key={call.id} call={call} now={now} />
			))}
		</>
	);
};

/**
 * Calls of one order with live wait and talk timers. The subscription starts
 * only when the component is rendered, so it is mounted with its tab.
 */
const OrderCalls: React.FC<OrderCalls.Props> = ({ orderId }) => {
	const { t } = useTranslation();

	return (
		<Root>
			{orderId && orderId > 0 ? (
				<List key={orderId} orderId={orderId} />
			) : (
				<Empty>{t("change_history.calls.empty")}</Empty>
			)}
		</Root>
	);
};

declare namespace OrderCalls {
	interface Props {
		orderId?: number | null;
	}
}

export default memo(OrderCalls);
